/**
 * VisitNotesPage - Complete appointment with post-visit notes
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, 
  Calendar,
  CheckCircle2,
  Stethoscope,
  FileText,
  Pill,
  TestTube,
  UserPlus,
  CalendarPlus,
  StickyNote,
  Paperclip,
  Loader2,
  Save,
  X,
  MessageSquareText,
  ListChecks
} from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import API from '@/utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 30%, #DCFCE7 60%, #D1FAE5 100%)',
};

const VisitNotesPage = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const { isDemoMode } = useDemo();
  
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    doctorSaid: '',
    questionsAnswers: '',
    diagnosis: '',
    instructions: '',
    medicationChanges: '',
    testsOrdered: '',
    referrals: '',
    followUpNeeded: false,
    followUpDate: '',
    nextSteps: '',
    additionalNotes: '',
  });

  useEffect(() => {
    fetchAppointment();
  }, [appointmentId]);

  const fetchAppointment = async () => {
    if (isDemoMode) {
      setLoading(false);
      return;
    }

    try {
      const response = await API.get(`/medical/appointment-preps/${appointmentId}`);
      setAppointment(response.data);
      
      // Pre-fill if visit notes exist
      if (response.data.visitNotes) {
        setFormData({
          doctorSaid: response.data.visitNotes.doctorSaid || '',
          questionsAnswers: response.data.visitNotes.questionsAnswers || '',
          diagnosis: response.data.visitNotes.diagnosis || '',
          instructions: response.data.visitNotes.instructions || '',
          medicationChanges: response.data.visitNotes.medicationChanges || '',
          testsOrdered: response.data.visitNotes.testsOrdered || '',
          referrals: response.data.visitNotes.referrals || '',
          followUpNeeded: response.data.visitNotes.followUpNeeded || false,
          followUpDate: response.data.visitNotes.followUpDate || '',
          nextSteps: response.data.visitNotes.nextSteps || '',
          additionalNotes: response.data.visitNotes.additionalNotes || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch appointment:', error);
      toast.error('Failed to load appointment');
      navigate('/medical');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (createFollowUp = false) => {
    if (isDemoMode) {
      toast.success('Visit notes saved (demo)');
      if (createFollowUp) {
        navigate('/medical/prepare');
      } else {
        navigate('/medical');
      }
      return;
    }

    setSaving(true);
    try {
      await API.post(`/medical/appointment-preps/${appointmentId}/complete`, {
        doctorSaid: formData.doctorSaid,
        questionsAnswers: formData.questionsAnswers,
        diagnosis: formData.diagnosis,
        instructions: formData.instructions,
        medicationChanges: formData.medicationChanges,
        testsOrdered: formData.testsOrdered,
        referrals: formData.referrals,
        followUpNeeded: formData.followUpNeeded,
        followUpDate: formData.followUpDate || null,
        nextSteps: formData.nextSteps,
        additionalNotes: formData.additionalNotes,
        attachments: [],
      });
      
      toast.success('Appointment marked as completed');
      
      if (createFollowUp) {
        // Navigate to new appointment prep with doctor pre-filled
        const params = new URLSearchParams();
        if (appointment.doctorName) params.set('doctor', appointment.doctorName);
        if (appointment.doctorId) params.set('doctorId', appointment.doctorId);
        if (formData.followUpDate) params.set('date', formData.followUpDate);
        navigate(`/medical/prepare?${params.toString()}`);
      } else {
        navigate('/medical');
      }
    } catch (error) {
      console.error('Failed to save visit notes:', error);
      toast.error('Failed to save visit notes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/medical/appointment/${appointmentId}`);
  };
  
  const handleReturnWithoutCompleting = () => {
    navigate('/medical');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: THEME.bgGradient }}>
        <div className="text-center">
          <p className="text-slate-600 mb-4">Appointment not found</p>
          <Button onClick={() => navigate('/medical')}>Back to Medical</Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="visit-notes-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-green-100">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Complete Visit</h1>
              <p className="text-xs text-slate-500">Add notes from your appointment</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Appointment Summary */}
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-medium text-slate-800">
                  {appointment.doctorName || 'Doctor Appointment'}
                </h2>
                <p className="text-sm text-slate-600">
                  {appointment.appointmentDate && format(parseISO(appointment.appointmentDate), 'MMMM d, yyyy')}
                  {appointment.appointmentTime && ` at ${appointment.appointmentTime}`}
                </p>
                {appointment.reason && (
                  <p className="text-xs text-slate-500 mt-1">{appointment.reason}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Visit Notes Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-green-600" />
              What did the doctor say?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Textarea
                value={formData.doctorSaid}
                onChange={(e) => setFormData({ ...formData, doctorSaid: e.target.value })}
                placeholder="Summary of what the doctor told you..."
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Doctor's Answers to My Questions */}
        {appointment.questions && appointment.questions.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-blue-600" />
                Doctor&apos;s Answers to My Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.questionsAnswers}
                onChange={(e) => setFormData({ ...formData, questionsAnswers: e.target.value })}
                placeholder="Record the doctor's answers to your prepared questions..."
                rows={4}
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Diagnosis & Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="diagnosis">Diagnosis or Explanation</Label>
              <Textarea
                id="diagnosis"
                value={formData.diagnosis}
                onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                placeholder="What was the diagnosis?"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="What did the doctor tell you to do?"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Pill className="h-4 w-4 text-purple-600" />
              Medication Changes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.medicationChanges}
              onChange={(e) => setFormData({ ...formData, medicationChanges: e.target.value })}
              placeholder="Any new medications, dosage changes, or medications stopped?"
              rows={3}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TestTube className="h-4 w-4 text-cyan-600" />
              Tests & Referrals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="testsOrdered">Tests or Labs Ordered</Label>
              <Textarea
                id="testsOrdered"
                value={formData.testsOrdered}
                onChange={(e) => setFormData({ ...formData, testsOrdered: e.target.value })}
                placeholder="Blood work, imaging, other tests..."
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="referrals">Referrals</Label>
              <Textarea
                id="referrals"
                value={formData.referrals}
                onChange={(e) => setFormData({ ...formData, referrals: e.target.value })}
                placeholder="Specialists or other providers you were referred to..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarPlus className="h-4 w-4 text-amber-600" />
              Follow-up
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="followUpNeeded">Follow-up appointment needed</Label>
              <Switch
                id="followUpNeeded"
                checked={formData.followUpNeeded}
                onCheckedChange={(v) => setFormData({ ...formData, followUpNeeded: v })}
              />
            </div>
            
            {formData.followUpNeeded && (
              <div>
                <Label htmlFor="followUpDate">Follow-up Date</Label>
                <Input
                  id="followUpDate"
                  type="date"
                  value={formData.followUpDate}
                  onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-indigo-600" />
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.nextSteps}
              onChange={(e) => setFormData({ ...formData, nextSteps: e.target.value })}
              placeholder="What you need to do next..."
              rows={2}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-slate-600" />
              Additional Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.additionalNotes}
              onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
              placeholder="Anything else you want to remember..."
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3 pt-4">
          <div className="flex gap-3">
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Visit Notes
            </Button>
          </div>
          
          {formData.followUpNeeded && (
            <Button
              variant="outline"
              className="w-full text-green-600 border-green-200 hover:bg-green-50"
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Save & Add Follow-Up Appointment
            </Button>
          )}
          
          <div className="flex gap-3">
            <Button
              variant="ghost"
              className="flex-1 text-slate-500"
              onClick={handleReturnWithoutCompleting}
            >
              Return Without Completing
            </Button>
            <Button
              variant="ghost"
              className="flex-1 text-slate-500"
              onClick={handleCancel}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VisitNotesPage;
